import { createContext, useContext, useEffect, useMemo, useRef } from 'react';

import { normalizeContainerErrorStatus } from '../../../utils/network/normalize-container-error-status';

type PageContainerErrorRetryFn = () => void | Promise<void>;

export interface IPageContainerRouteErrorState {
  errors: unknown[];
  isLoading: boolean;
  hasData: boolean;
  onRetry?: PageContainerErrorRetryFn;
}

type PageContainerRegisterErrorState = (
  state: IPageContainerRouteErrorState | null,
) => void;

export const PageContainerErrorHandlerContext =
  createContext<PageContainerRegisterErrorState | null>(null);

export function useRegisterPageContainerError(
  state: IPageContainerRouteErrorState | null,
) {
  const register = useContext(PageContainerErrorHandlerContext);
  const errors = state?.errors ?? [];
  const isLoading = state?.isLoading ?? false;
  const hasData = state?.hasData ?? false;
  const onRetry = state?.onRetry;
  const isStatePresent = state != null;

  const lastRegistrationRef = useRef<{
    signature: string;
    isLoading: boolean;
    hasData: boolean;
    onRetry?: PageContainerErrorRetryFn;
  } | null>(null);

  const signature = useMemo(
    () =>
      errors
        .map((error) => normalizeContainerErrorStatus(error) ?? 'unknown')
        .join('|'),
    [errors],
  );

  useEffect(() => {
    return () => {
      register?.(null);
      lastRegistrationRef.current = null;
    };
  }, [register]);

  useEffect(() => {
    if (!register) {
      return;
    }

    if (!isStatePresent) {
      if (lastRegistrationRef.current != null) {
        register(null);
        lastRegistrationRef.current = null;
      }
      return;
    }

    const lastRegistration = lastRegistrationRef.current;
    const didChange =
      !lastRegistration ||
      lastRegistration.signature !== signature ||
      lastRegistration.isLoading !== isLoading ||
      lastRegistration.hasData !== hasData ||
      lastRegistration.onRetry !== onRetry;

    if (didChange) {
      register({
        errors,
        isLoading,
        hasData,
        onRetry,
      });
      lastRegistrationRef.current = {
        signature,
        isLoading,
        hasData,
        onRetry,
      };
    }
  }, [
    errors,
    hasData,
    isLoading,
    isStatePresent,
    onRetry,
    register,
    signature,
  ]);
}
